import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { ssrDocument } from "@/utils/ssrUtils";

const mediaId = "9om4xfe7hh";

const VideoDemoSection = () => {
  const playerRef = useRef<HTMLDivElement>(null);
  const [isPlayerLoaded, setIsPlayerLoaded] = useState(false);
  const sectionRef = useRef<HTMLElement | null>(null);
  const scriptsLoaded = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const initWistia = () => {
      if (scriptsLoaded.current) return;
      scriptsLoaded.current = true;

      // Set up wistiaOptions for the custom element
      if (!(window as any).wistiaOptions) {
        (window as any).wistiaOptions = {};
      }

      // Configure the player with the media ID
      (window as any).wistiaOptions[mediaId] = {
        onReady: () => {
          setIsPlayerLoaded(true);
        }
      };

      // Load Wistia scripts sequentially - SSR safe
      const loadWistiaScripts = () => {
        const script1 = ssrDocument.createElement("script") as HTMLScriptElement;
        if (!script1) return;
        
        script1.src = "https://fast.wistia.com/player.js";
        script1.async = true;
        
        script1.onload = () => {
          // Script 1 loaded, now load script 2
          const script2 = ssrDocument.createElement("script") as HTMLScriptElement;
          if (!script2) return;
          
          script2.src = `https://fast.wistia.com/embed/${mediaId}.js`;
          script2.async = true;
          script2.type = "module";
          ssrDocument.body?.appendChild(script2);
        };
        
        ssrDocument.body?.appendChild(script1);
      };

      loadWistiaScripts();
    };

    let observer: IntersectionObserver | null = null;

    // Lazy-load when section approaches viewport
    if ('IntersectionObserver' in window && sectionRef.current) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting || entry.intersectionRatio > 0) {
              initWistia();
              observer && observer.disconnect();
            }
          });
        },
        { root: null, rootMargin: '150px', threshold: 0.1 }
      );
      observer.observe(sectionRef.current);
    } else {
      // Fallback: load immediately
      initWistia();
    }

    // Cleanup function
    return () => {
      try {
        observer && observer.disconnect();
        // Remove scripts
        const scripts = ssrDocument.querySelectorAll(`script[src*="wistia.com"]`);
        scripts.forEach(script => {
          ssrDocument.body?.removeChild(script);
        });
        
        // Clean up wistiaOptions
        if ((window as any).wistiaOptions && (window as any).wistiaOptions[mediaId]) {
          delete (window as any).wistiaOptions[mediaId];
        }
        scriptsLoaded.current = false;
      } catch (e) {
        console.error("Error cleaning up Wistia resources:", e);
      }
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="section-padding bg-[#050505] flex justify-center"
      aria-label="CodeRide Demo Video"
    >
      <style>
        {`
          wistia-player[media-id='${mediaId}']:not(:defined) {
            background: center / contain no-repeat url('https://fast.wistia.com/embed/medias/${mediaId}/swatch');
            display: block;
            filter: blur(5px);
            padding-top: 61.25%;
          }
          wistia-player[media-id='${mediaId}'] {
            filter: none !important;
          }
        `}
      </style>
      <div className="max-w-7xl w-full flex flex-col md:flex-row items-center gap-8">
        {/* Text Section */}
        <div className="md:w-1/3 text-white space-y-4 flex flex-col justify-center">
          <h2 className="text-3xl font-bold">
            Watch CodeRide Transform Your Vibe Coding Workflow
          </h2>
          <p className="text-base text-white/90 leading-relaxed">
            See how CodeRide revolutionizes project management and <span className="bg-[#7867EE] text-white px-2 py-1 rounded font-mono">vibe coding</span> by
            empowering AI agents with <strong className="font-semibold text-white">complete context awareness</strong> and clear
            instructions. Seamless task execution, intelligent code generation,
            and effortless integration—all without the typical back-and-forth
            explanations that slow down your development process.
          </p>
          <div>
            <Button
              variant="primary"
              size="lg"
              showArrow={true}
              onClick={() => window.open('https://app.coderide.ai/auth/sign-up', '_blank')}
              className="mt-4"
            >
              Get Started Now
            </Button>
          </div>
        </div>

        {/* Video Section */}
        <div className="md:w-2/3 w-full rounded-lg overflow-hidden shadow-lg relative">
          {/* Aspect ratio container 16:9 */}
          <div className="relative pb-[61.25%] w-full h-0 rounded-lg overflow-visible">
            <wistia-player
              media-id={mediaId}
              wistia-popover={true}
              className="absolute top-0 left-0 w-full h-full"
              ref={playerRef as any}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default VideoDemoSection;
